function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from 'react';
import classNames from 'classnames';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { updateWalletBalance } from '@profile/src/redux/remoteActions';
import { ellipsisMiddle } from 'src/utils/text';
import DropdownList from './DropdownList';
import { menuList, walletProviderMap } from './config';
import styles from './styles.module.scss';
import icCopy from 'assets/svg/ic_copy.svg';
import { formatCurrency, formatCurrencyInFiat } from '@wolffun/system-currency';
import { useTooltip } from 'src/Components/TooltipProvider';
import { TooltipDirection } from 'src/Components/TooltipProvider';
import { getPreferredWalletProviderName } from 'src/utils/session';
import { copyToClipboard } from 'src/utils/copyToClipboard';
import { useTestId } from '@wolffun/testable-id'; // eslint-disable-next-line @typescript-eslint/ban-types

const ProfilePanel = /*#__PURE__*/React.forwardRef( // eslint-disable-next-line max-statements
(_, ref) => {
  const {
    generateTestIdProps
  } = useTestId();
  const dispatch = useTypedDispatch();
  const [collapsed, setCollapsed] = React.useState(true);
  const [position, setPosition] = React.useState({
    top: 0,
    right: 0
  });
  const imgRef = React.useRef(null);
  const spanRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const tooltip = useTooltip();
  const username = useTypedSelector(state => {
    var _state$userProfile$us;

    return ((_state$userProfile$us = state.userProfile.user) === null || _state$userProfile$us === void 0 ? void 0 : _state$userProfile$us.username) || '';
  });
  const account = useTypedSelector(state => {
    var _state$connectWallet$;

    return ((_state$connectWallet$ = state.connectWallet.accounts) === null || _state$connectWallet$ === void 0 ? void 0 : _state$connectWallet$[0]) || '';
  });
  const thcBalance = useTypedSelector(state => state.connectWallet.thcBalance);
  const walletProvider = getPreferredWalletProviderName();
  const iconProvider = walletProvider ? walletProviderMap[walletProvider] : undefined;
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) //hack because TS not know exactly what inside target. More information: https://stackoverflow.com/questions/43842057/detect-if-click-was-inside-react-component-or-not-in-typescript
      ) {
        closeDropdown();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);
  React.useEffect(() => {
    // periodically updateWalletBalance every 30sec
    if (!collapsed) {
      dispatch(updateWalletBalance());
      const timeout = setInterval(() => dispatch(updateWalletBalance()), 30 * 1000);
      return () => clearInterval(timeout);
    }

    return undefined;
  }, [collapsed]);

  const toggleDropdown = position => {
    setPosition(position);
    setCollapsed(!collapsed);
  };

  React.useImperativeHandle(ref, () => {
    return {
      toggleDropdown
    };
  });

  const closeDropdown = () => {
    setCollapsed(true);
  };

  const onPress = (top, left, position) => {
    tooltip.show('Copied', {
      top,
      left
    }, position, {
      backgroundColor: '#261858',
      textColor: '#fff',
      delay: 1500
    });
  };

  const renderChildMenu = ({
    key,
    name,
    handlePress
  }) => {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.itemChild,
      key: key,
      onClick: () => {
        handlePress === null || handlePress === void 0 ? void 0 : handlePress({
          dispatch
        });
        closeDropdown();
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: styles.name
    }, name));
  };

  return collapsed ? null : /*#__PURE__*/React.createElement(DropdownList, _extends({
    ref: dropdownRef,
    className: classNames(styles.dropdown, styles.anim),
    top: position.top,
    right: position.right
  }, generateTestIdProps('user-menu')), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.name
  }, generateTestIdProps('user-name')), username), /*#__PURE__*/React.createElement("div", {
    onClick: () => {
      copyToClipboard(account);
    },
    className: styles.divCode
  }, /*#__PURE__*/React.createElement("img", {
    className: styles.icon,
    title: walletProvider,
    src: iconProvider
  }), /*#__PURE__*/React.createElement("span", _extends({
    ref: spanRef,
    onClick: () => {
      if (spanRef.current) {
        const position = spanRef.current.getBoundingClientRect();
        onPress(position.bottom - 30, position.left - position.width / 2 - 20, TooltipDirection.LEFT);
      }
    },
    className: styles.address
  }, generateTestIdProps('wallet-address')), ellipsisMiddle(account)), /*#__PURE__*/React.createElement("img", {
    ref: imgRef,
    className: styles.icCopy,
    onClick: () => {
      if (imgRef.current) {
        const position = imgRef.current.getBoundingClientRect();
        onPress(position.bottom + 10, position.left + position.width / 2, TooltipDirection.BOTTOM);
      }
    },
    width: 24,
    height: 24,
    src: icCopy
  })), /*#__PURE__*/React.createElement("div", _extends({
    className: styles.balanceInfo
  }, generateTestIdProps('user-balance')), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Balance"), /*#__PURE__*/React.createElement("span", {
    className: styles.coin
  }, thcBalance && formatCurrency(thcBalance, {
    withCurrency: true,
    trimWholeFraction: true
  })), /*#__PURE__*/React.createElement("span", {
    className: styles.money
  }, thcBalance && formatCurrencyInFiat(thcBalance, {
    withCurrency: true,
    trimWholeFraction: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.menu
  }, menuList.map(({
    name,
    icon,
    handlePress,
    key,
    children
  }) => /*#__PURE__*/React.createElement("div", {
    key: key,
    className: styles.divItem
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.item, (children === null || children === void 0 ? void 0 : children.length) && styles.hasChildren),
    onClick: () => {
      handlePress === null || handlePress === void 0 ? void 0 : handlePress({
        dispatch
      });
      closeDropdown();
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.parentMenu
  }, icon ? /*#__PURE__*/React.createElement("img", {
    className: styles.icon,
    src: icon
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: styles.itemName
  }, name)), (children === null || children === void 0 ? void 0 : children.length) && /*#__PURE__*/React.createElement("div", {
    className: styles.childMenu
  }, children.map(menu => renderChildMenu(menu))))))));
});
ProfilePanel.displayName = 'ProfileSettings';
export default ProfilePanel;